import { FC, useCallback, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useAppDispatch } from "src/redux";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { tApplicationRepPath } from "src/redux/Application/types";
import { tReduxState } from "src/redux/types";
import { aPriceRange, aRepBrandCount, aRepType, aStates, hearAboutUsOptions, tpRepType } from "../util/comboBoxData";
import { normalizeStateName } from "../util/gFormatting";
import ComboBox from "./ComboBox";
import {
  FormComboBoxInput,
  TextFormInput,
  tValidType,
  ValidatedComboBoxInput,
  ValidatedTextFormInput,
} from "./FormInputs";
import RepApplicationCategoryOptions from "./RepApplicationCategoryOptions";

const stateNormalizer = (val: string) => normalizeStateName(val) || "State";
const nameSelector = ({ ApplicationReducer }: tReduxState) =>
  ApplicationReducer.type === "rep" ? ApplicationReducer.companyName : "";
const rBrandQuantity = ({ ApplicationReducer }: tReduxState) =>
  ApplicationReducer.type === "rep" ? ApplicationReducer.brandQuantity : "1-5";
const rAddress1 = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.address1;
const rAddress2 = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.address2;
const rCity = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.city;
const rState = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.state_or_province;
const rPostalCode = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.address.postal_code;
const rTaxId = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.taxId;
const rWebsite = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.website;
const rPrice = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.price;
const rNote = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.note;
const rHearAboutUs = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.hearAboutUs;
const rFacebook = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.socialMedia?.facebook;
const rInstagram = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.socialMedia?.instagram;
const rLinkedIn = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.socialMedia?.linkedin;
const _isShown = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.isShown;

export const RepApplication: FC<{
  isValid: tValidType;
  setValid: React.Dispatch<React.SetStateAction<tValidType>>;
}> = ({ isValid, setValid }) => {
  const dispatch = useAppDispatch();
  // const rAgency = useSelector(
  //   ({ ApplicationReducer }) => ApplicationReducer.type === "rep" && ApplicationReducer.agency,
  // );

  const [sRepType, setRepType] = useState<tpRepType>();

  // if (rAgency === true) {
  //   setRepType("Sales Rep Agency");
  // } else if (rAgency === false) {
  //   setRepType("Independent Sales Rep");
  // } else {
  //   setRepType("Select Rep Type");
  // }

  const aRepTypeChange = useCallback(
    ({ myValue }) => {
      setRepType(myValue);

      if (myValue === "Sales Rep Agency") {
        dispatch({
          type: "UPDATE_REP_APPLICATION",
          applicationPart: { agency: true },
        });
      } else {
        dispatch({
          type: "UPDATE_REP_APPLICATION",
          applicationPart: { agency: false },
        });
      }

      // if (rAgency === true) {
      //   setRepType("Sales Rep Agency");
      // } else if (rAgency === false) {
      //   setRepType("Independent Sales Rep");
      // } else {
      //   setRepType("Select Rep Type");
      // }
    },
    [dispatch],
  );

  return (
    <>
      <View style={styles.gApplyTablePageContainer}>
        <View style={styles.gFormContainer}>
          <Text style={styles.sectionTitle}>Company Information</Text>
          <ValidatedTextFormInput<tApplicationRepPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleViewStyle={styles.rowNoMargin}
            titleProps={{ style: styles.P08_01_InputHeader }}
            controller={isValid}
            myTitle="Company Name *"
            uniqueName="companyName"
            rules={{ required: { message: "Required" } }}
            textInputProps={{
              style: styles.gLinkBankInput,
              placeholder: "Company Name",
              autoCompleteType: "organization",
            }}
            dispatchAction={{ type: "UPDATE_REP_APPLICATION", applicationPart: { companyName: "" } }}
            dispatchPath="applicationPart.companyName"
            setController={setValid}
            selectorFunction={nameSelector}
            isShown={_isShown}
          />
          <ValidatedTextFormInput<tApplicationRepPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleViewStyle={styles.rowNoMargin}
            titleProps={{ style: styles.P08_01_InputHeader }}
            controller={isValid}
            rules={{ required: { message: "Required" } }}
            myTitle="Address *"
            uniqueName="address1"
            textInputProps={{
              style: styles.gLinkBankInput,
              placeholder: "1111 Street name",
              autoCompleteType: "address-line1",
            }}
            dispatchAction={{
              type: "UPDATE_REP_APPLICATION",
              applicationPart: { address: { address1: "" } },
            }}
            dispatchPath="applicationPart.address.address1"
            setController={setValid}
            selectorFunction={rAddress1}
            isShown={_isShown}
          />
          <TextFormInput<tApplicationRepPath>
            wrapperStyle={styles.gFormRowNoMarginSingle}
            titleStyle={styles.P08_01_InputHeader}
            myTitle="Address Line 2"
            textInputProps={{
              placeholder: "E.g. Apt. 1",
              autoCompleteType: "address-line2",
              style: styles.gLinkBankInput,
            }}
            dispatchAction={{
              type: "UPDATE_REP_APPLICATION",
              applicationPart: { address: { address2: "" } },
            }}
            dispatchPath="applicationPart.address.address2"
            selectorFunction={rAddress2}
          />
          <View style={styles.gFormRowNoMarginZIndex}>
            <ValidatedTextFormInput<tApplicationRepPath>
              wrapperStyle={styles.gLinkBankFormItem}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              myTitle="City *"
              uniqueName="cityName"
              rules={{ required: { message: "Required" } }}
              controller={isValid}
              textInputProps={{
                style: styles.gLinkBankTripleInput,
                placeholder: "City Name",
              }}
              dispatchAction={{
                type: "UPDATE_REP_APPLICATION",
                applicationPart: { address: { city: "" } },
              }}
              dispatchPath="applicationPart.address.city"
              selectorFunction={rCity}
              setController={setValid}
              isShown={_isShown}
            />
            <ValidatedComboBoxInput<tApplicationRepPath>
              wrapperStyle={styles.gLinkBankFormItemZIndex}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              controller={isValid}
              ComboOptions={aStates}
              rules={{ required: { message: "Required" } }}
              uniqueName="State"
              myTitle="State *"
              placeholder="State"
              normalizerFunction={stateNormalizer}
              autoComplete="address-level1"
              isShown={_isShown}
              setController={setValid}
              dispatchAction={{
                type: "UPDATE_REP_APPLICATION",
                applicationPart: { address: { state_or_province: "" } },
              }}
              dispatchPath="applicationPart.address.state_or_province"
              selectorFunction={rState}
            />
            <ValidatedTextFormInput<tApplicationRepPath>
              wrapperStyle={styles.gLinkBankFormItemBottomMargin}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              controller={isValid}
              uniqueName="ZipCode"
              myTitle="Zip Code *"
              rules={{
                required: { message: "Required" },
                pattern: { pattern: /\d{5}/, message: "Enter 5 digit zipcode" },
              }}
              textInputProps={{
                placeholder: "12345",
                style: styles.gLinkBankTripleInput,
                maxLength: 5,
                autoCompleteType: "postal-code",
              }}
              dispatchAction={{
                type: "UPDATE_REP_APPLICATION",
                applicationPart: { address: { postal_code: "" } },
              }}
              dispatchPath="applicationPart.address.postal_code"
              setController={setValid}
              isShown={_isShown}
              selectorFunction={rPostalCode}
            />
          </View>
          <View style={styles.gFormRowNoMargin}>
            <TextFormInput<tApplicationRepPath>
              wrapperStyle={styles.gLinkBankFormItem}
              titleStyle={styles.P08_01_InputHeader}
              myTitle="Tax ID #"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Company Tax Id",
              }}
              dispatchAction={{
                type: "UPDATE_REP_APPLICATION",
                applicationPart: { taxId: "" },
              }}
              dispatchPath="applicationPart.taxId"
              selectorFunction={rTaxId}
            />
            <ValidatedTextFormInput<tApplicationRepPath>
              wrapperStyle={styles.gLinkBankFormItemNoMargin}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              myTitle="Website *"
              uniqueName="website"
              rules={{
                required: { message: "Required" },
              }}
              controller={isValid}
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Company Website",
                autoCompleteType: "url",
              }}
              dispatchAction={{
                type: "UPDATE_REP_APPLICATION",
                applicationPart: { website: "" },
              }}
              dispatchPath="applicationPart.website"
              setController={setValid}
              selectorFunction={rWebsite}
              isShown={_isShown}
            />
          </View>
          <View style={styles.gFormRowNoMargin}>
            <ValidatedTextFormInput<tApplicationRepPath>
              wrapperStyle={styles.gLinkBankFormItem}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              controller={isValid}
              myTitle="Facebook"
              uniqueName="facebook"
              textInputProps={{
                placeholder: "www.facebook.com/storename",
                style: styles.gLinkBankInput,
              }}
              dispatchAction={{
                type: "UPDATE_REP_APPLICATION",
                applicationPart: { socialMedia: { facebook: "" } },
              }}
              dispatchPath="applicationPart.socialMedia.facebook"
              setController={setValid}
              selectorFunction={rFacebook}
              isShown={_isShown}
            />
            <ValidatedTextFormInput<tApplicationRepPath>
              wrapperStyle={styles.gLinkBankFormItem}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              myTitle="Instagram"
              uniqueName="instagram"
              controller={isValid}
              textInputProps={{
                placeholder: "www.instagram.com/storename",
                style: styles.gLinkBankInput,
              }}
              dispatchAction={{
                type: "UPDATE_REP_APPLICATION",
                applicationPart: { socialMedia: { instagram: "" } },
              }}
              dispatchPath="applicationPart.socialMedia.instagram"
              selectorFunction={rInstagram}
              setController={setValid}
              isShown={_isShown}
            />
            <ValidatedTextFormInput<tApplicationRepPath>
              wrapperStyle={styles.gLinkBankFormItemNoMargin}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              controller={isValid}
              myTitle="LinkedIn"
              uniqueName="linkedin"
              textInputProps={{
                placeholder: "www.LinkedIn.com/storename",
                style: styles.gLinkBankInput,
              }}
              dispatchAction={{
                type: "UPDATE_REP_APPLICATION",
                applicationPart: { socialMedia: { linkedin: "" } },
              }}
              dispatchPath="applicationPart.socialMedia.linkedin"
              setController={setValid}
              selectorFunction={rLinkedIn}
              isShown={_isShown}
            />
          </View>
        </View>
      </View>

      {/* Second View */}
      <View style={styles.gTablePageContainerBottom}>
        <View style={styles.gFormContainer}>
          <Text style={styles.sectionTitle}>About Your Business</Text>
          <View style={styles.gFormRowNoMarginZIndex}>
            <View style={[styles.gLinkBankFormItemZIndex, styles.zIndexFourteen]}>
              <Text style={styles.P08_01_InputHeader}>Rep Type *</Text>
              <View style={styles.applyComboBoxView2}>
                <ComboBox
                  value={sRepType}
                  onSelect={aRepTypeChange}
                  myOptions={aRepType}
                  myPlaceholder="E.g. Gift Shop"
                />
              </View>
            </View>
            <FormComboBoxInput<tApplicationRepPath>
              wrapperStyle={[styles.gLinkBankFormItemZIndex, styles.zIndexTwelve]}
              titleStyle={styles.P08_01_InputHeader}
              ComboOptions={aPriceRange}
              dispatchAction={{
                type: "UPDATE_REP_APPLICATION",
                applicationPart: { price: "$" },
              }}
              dispatchPath="applicationPart.price"
              selectorFunction={rPrice}
              placeholder={"Please select a price point"}
              myTitle="Average MSRP of your Brands *"
            />
            <FormComboBoxInput<tApplicationRepPath>
              wrapperStyle={styles.gLinkBankFormItemNoMarginZIndex}
              titleStyle={styles.P08_01_InputHeader}
              ComboOptions={aRepBrandCount}
              dispatchAction={{
                type: "UPDATE_REP_APPLICATION",
                applicationPart: { brandQuantity: "1-5" },
              }}
              dispatchPath="applicationPart.brandQuantity"
              selectorFunction={rBrandQuantity}
              placeholder={"Number Including E-Commerce Site"}
              myTitle="How many Retail Locations do you have?"
            />
          </View>

          <RepApplicationCategoryOptions />

          <TextFormInput<tApplicationRepPath>
            wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
            titleStyle={styles.P08_01_InputHeader}
            myTitle="More Information"
            textInputProps={{
              multiline: true,
              numberOfLines: 12,
              style: styles.gLinkBankInputBox,
              placeholder: "Tell us a little more about you and your business.",
            }}
            dispatchAction={{
              type: "UPDATE_REP_APPLICATION",
              applicationPart: { note: "" },
            }}
            dispatchPath="applicationPart.note"
            selectorFunction={rNote}
          />
          <ValidatedComboBoxInput<tApplicationRepPath>
            wrapperStyle={[styles.gLinkBankFormItemZIndex, { marginBottom: kStyleConst.kSpacing }]}
            titleViewStyle={styles.rowNoMargin}
            titleProps={{ style: styles.P08_01_InputHeader }}
            controller={isValid}
            ComboOptions={hearAboutUsOptions}
            rules={{ required: { message: "Required" } }}
            uniqueName="HearAbout"
            myTitle="How did you hear about us? *"
            placeholder="Select how did you hear about us"
            isShown={_isShown}
            setController={setValid}
            dispatchAction={{
              type: "UPDATE_REP_APPLICATION",
              applicationPart: { hearAboutUs: "social-media" },
            }}
            dispatchPath="applicationPart.hearAboutUs"
            selectorFunction={rHearAboutUs}
          />
        </View>
      </View>
    </>
  );
};

export default RepApplication;

const styles = StyleSheet.create({
  P08_01_InputHeader: {
    ...kFonts.poppinsRegular,
    color: kColors.darkGray,
    letterSpacing: 1.1,
  },
  applyComboBoxView2: {
    flexDirection: "row",
    marginTop: -3,
    paddingVertical: 2 * kStyleConst.kSpacing,
    zIndex: 10,
  },
  gApplyTablePageContainer: {
    backgroundColor: kColors.white,
    borderRadius: 8,
    flexGrow: 1,
    marginTop: 7 * kStyleConst.kSpacing,
    zIndex: 2,
  },
  gFormContainer: {
    marginBottom: 12 * kStyleConst.kSpacing,
    marginHorizontal: 13 * kStyleConst.kSpacing,
    marginTop: 12 * kStyleConst.kSpacing,
    zIndex: 1,
  },
  gFormRowNoMargin: {
    alignItems: "flex-start",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gFormRowNoMarginSingle: {
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gFormRowNoMarginValidatedSingle: {
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gFormRowNoMarginZIndex: {
    alignItems: "flex-start",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minWidth: kStyleConst.kApplyMinWidth,
    zIndex: 10,
  },
  gLinkBankFormItem: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginRight: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemBottomMargin: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemNoMargin: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemNoMarginZIndex: {
    flex: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
    zIndex: 10,
  },
  gLinkBankFormItemZIndex: {
    flex: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginRight: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
    zIndex: 100000,
  },
  gLinkBankInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginLeft: 0,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gLinkBankInputBox: {
    borderColor: kColors.brandPink,
    borderRadius: 2,
    borderWidth: 1,
    flex: 1,
    height: kStyleConst.kApplicationNotesBoxHeight,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    outlineWidth: 0,
    paddingLeft: 2 * kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gLinkBankTripleInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gTablePageContainerBottom: {
    backgroundColor: kColors.white,
    borderRadius: 8,
    flexGrow: 1,
    marginVertical: 7 * kStyleConst.kSpacing,
    zIndex: 1,
  },
  rowNoMargin: {
    flexDirection: "row",
    marginTop: 0,
  },
  sectionTitle: {
    ...kFonts.ralewayLargeLight,
    alignSelf: "flex-start",
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  zIndexFourteen: {
    zIndex: 14,
  },
  zIndexTwelve: {
    zIndex: 12,
  },
});
